import React from 'react';
import { motion } from 'framer-motion';

const QivratechLogo = ({
  className = "",
  size = "default",
  variant = "full", // "full", "mark", "text"
  animated = false,
  theme = "dark" // "dark", "light"
}) => {
  // Mobile-responsive size configurations
  const sizes = {
    small: {
      logo: { width: 100, height: 32 },
      q: { width: 24, height: 24 },
      text: "text-sm sm:text-lg",
      spacing: "gap-1 sm:gap-2"
    },
    default: {
      logo: { width: 140, height: 48 },
      q: { width: 36, height: 36 },
      text: "text-lg sm:text-xl lg:text-2xl",
      spacing: "gap-2 sm:gap-3"
    },
    large: {
      logo: { width: 180, height: 64 },
      q: { width: 48, height: 48 },
      text: "text-xl sm:text-2xl lg:text-3xl",
      spacing: "gap-3 sm:gap-4"
    },
    hero: {
      logo: { width: 240, height: 96 },
      q: { width: 72, height: 72 },
      text: "text-3xl sm:text-4xl lg:text-5xl",
      spacing: "gap-4 sm:gap-6"
    }
  };

  const currentSize = sizes[size] || sizes.default;

  // Theme-based colors
  const themes = {
    dark: {
      primary: "#0ea5e9",
      secondary: "#a855f7",
      accent: "#22d3ee",
      text: "#ffffff",
      glow: "rgba(14, 165, 233, 0.5)"
    },
    light: {
      primary: "#0284c7",
      secondary: "#7c3aed",
      accent: "#0891b2",
      text: "#1f2937",
      glow: "rgba(2, 132, 199, 0.3)"
    }
  };

  const colors = themes[theme];

  // Animation variants
  const logoVariants = {
    initial: { scale: 1, rotateY: 0 },
    hover: {
      scale: 1.05,
      rotateY: 5,
      transition: { duration: 0.3, ease: "easeInOut" }
    },
    pulse: {
      scale: [1, 1.02, 1],
      transition: { duration: 2, repeat: Infinity, ease: "easeInOut" }
    }
  };

  const qVariants = {
    initial: { pathLength: 0, opacity: 0 },
    animate: {
      pathLength: 1,
      opacity: 1,
      transition: { duration: 2, ease: "easeInOut" }
    }
  };

  const nodeVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.8, delay: 0.5, ease: "easeOut" }
    },
    pulse: {
      scale: [1, 1.2, 1],
      opacity: [0.7, 1, 0.7],
      transition: { duration: 2, repeat: Infinity, ease: "easeInOut" }
    }
  };

  // Q Mark Component - Yin Yang Style with mobile optimization
  const QMark = ({ width, height }) => (
    <motion.svg
      width={width}
      height={height}
      viewBox="0 0 100 100"
      className="drop-shadow-lg"
      style={{
        filter: animated && window.innerWidth >= 768 ? `drop-shadow(0 0 10px ${colors.glow})` : 'none',
        willChange: animated ? 'transform' : 'auto'
      }}
      variants={logoVariants}
      initial="initial"
      animate={animated && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? "pulse" : "initial"}
      whileHover={window.innerWidth >= 768 ? "hover" : {}}
    >
      <defs>
        {/* Clean gradient for modern look */}
        <linearGradient id="qGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: colors.primary, stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: colors.secondary, stopOpacity: 1 }} />
        </linearGradient>

        {/* Subtle glow effect */}
        <filter id="subtleGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Outer circle */}
      <motion.circle
        cx="50"
        cy="50"
        r="48"
        fill="none"
        stroke={colors.primary}
        strokeWidth="2"
        variants={qVariants}
        initial="initial"
        animate="animate"
        filter="url(#subtleGlow)"
      />

      {/* Yin Yang divider line */}
      <motion.path
        d="M 50 2 Q 25 25 50 50 Q 75 75 50 98"
        fill="none"
        stroke={colors.primary}
        strokeWidth="2"
        strokeLinecap="round"
        variants={qVariants}
        initial="initial"
        animate="animate"
        filter="url(#subtleGlow)"
      />

      {/* Left Q */}
      <motion.g variants={nodeVariants} initial="initial" animate="animate">
        <text
          x="32"
          y="35"
          textAnchor="middle"
          className="font-bold text-lg"
          style={{
            fill: colors.text,
            fontSize: window.innerWidth < 640 ? '14px' : '18px',
            fontFamily: '"Space Grotesk", "Inter", sans-serif',
            fontWeight: '600'
          }}
        >
          Q
        </text>
      </motion.g>

      {/* Right Q */}
      <motion.g variants={nodeVariants} initial="initial" animate="animate">
        <text
          x="68"
          y="75"
          textAnchor="middle"
          className="font-bold text-lg"
          style={{
            fill: colors.text,
            fontSize: window.innerWidth < 640 ? '14px' : '18px',
            fontFamily: '"Space Grotesk", "Inter", sans-serif',
            fontWeight: '600'
          }}
        >
          Q
        </text>
      </motion.g>

      {/* Animated rotation for the whole symbol */}
      {animated && (
        <animateTransform
          attributeName="transform"
          type="rotate"
          values="0 50 50;360 50 50"
          dur="20s"
          repeatCount="indefinite"
        />
      )}
    </motion.svg>
  );

  // Full Logo Component
  if (variant === "mark") {
    return (
      <div className={`flex items-center ${className}`}>
        <QMark width={currentSize.q.width} height={currentSize.q.height} />
      </div>
    );
  }

  if (variant === "text") {
    return (
      <motion.div
        className={`relative ${className}`}
        style={{
          fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif',
          letterSpacing: '0.25em'
        }}
        variants={logoVariants}
        initial="initial"
        animate={animated ? "pulse" : "initial"}
        whileHover="hover"
      >
        {/* Main Text with Gradient */}
        <motion.span
          className={`${currentSize.text} bg-gradient-to-r from-white via-cyan-100 to-white bg-clip-text text-transparent relative z-10 font-extralight`}
          animate={animated ? {
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            textShadow: [
              '0 0 15px rgba(255, 255, 255, 0.4)',
              '0 0 25px rgba(0, 255, 255, 0.6)',
              '0 0 15px rgba(255, 255, 255, 0.4)'
            ]
          } : {}}
          transition={{
            backgroundPosition: { duration: 8, repeat: Infinity, ease: "linear" },
            textShadow: { duration: 3, repeat: Infinity, ease: "easeInOut" }
          }}
          style={{
            backgroundSize: '200% 100%',
            fontWeight: '200'
          }}
        >
          QIVRATECH
        </motion.span>

        {/* Subtle Glow Background */}
        <motion.span
          className={`${currentSize.text} absolute inset-0 bg-gradient-to-r from-cyan-400/20 via-blue-400/30 to-purple-400/20 bg-clip-text text-transparent blur-sm font-extralight`}
          animate={animated ? {
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.02, 1]
          } : {}}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif',
            fontWeight: '200',
            letterSpacing: '0.25em'
          }}
          aria-hidden="true"
        >
          QIVRATECH
        </motion.span>

        {/* Quantum Accent Lines */}
        {animated && (
          <>
            <motion.div
              className="absolute -bottom-1 left-0 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
              animate={{
                opacity: [0, 0.8, 0],
                scaleX: [0.8, 1.2, 0.8]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
              style={{ width: '100%' }}
            />
            <motion.div
              className="absolute -top-1 right-0 h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent"
              animate={{
                opacity: [0, 0.6, 0],
                scaleX: [0.6, 1, 0.6]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2
              }}
              style={{ width: '60%' }}
            />
          </>
        )}
      </motion.div>
    );
  }

  // Full logo (default)
  return (
    <motion.div
      className={`flex items-center ${currentSize.spacing} ${className}`}
      variants={logoVariants}
      initial="initial"
      animate={animated ? "pulse" : "initial"}
      whileHover="hover"
    >
      <QMark width={currentSize.q.width} height={currentSize.q.height} />

      {/* Enhanced Wordmark with Next-Gen Typography */}
      <motion.div
        className="relative"
        style={{
          fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif',
          letterSpacing: '0.25em'
        }}
      >
        {/* Main Text with Gradient */}
        <motion.span
          className={`${currentSize.text} bg-gradient-to-r from-white via-cyan-100 to-white bg-clip-text text-transparent relative z-10 font-extralight`}
          animate={animated ? {
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            textShadow: [
              '0 0 15px rgba(255, 255, 255, 0.4)',
              '0 0 25px rgba(0, 255, 255, 0.6)',
              '0 0 15px rgba(255, 255, 255, 0.4)'
            ]
          } : {}}
          transition={{
            backgroundPosition: { duration: 8, repeat: Infinity, ease: "linear" },
            textShadow: { duration: 3, repeat: Infinity, ease: "easeInOut" }
          }}
          style={{
            backgroundSize: '200% 100%',
            fontWeight: '200',
            fontVariationSettings: '"wght" 200, "slnt" 0'
          }}
        >
          QIVRATECH
        </motion.span>

        {/* Subtle Glow Background */}
        <motion.span
          className={`${currentSize.text} absolute inset-0 bg-gradient-to-r from-cyan-400/20 via-blue-400/30 to-purple-400/20 bg-clip-text text-transparent blur-sm font-extralight`}
          animate={animated ? {
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.02, 1]
          } : {}}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif',
            fontWeight: '200',
            letterSpacing: '0.25em'
          }}
          aria-hidden="true"
        >
          QIVRATECH
        </motion.span>

        {/* Quantum Accent Lines */}
        {animated && (
          <>
            <motion.div
              className="absolute -bottom-1 left-0 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
              animate={{
                opacity: [0, 0.8, 0],
                scaleX: [0.8, 1.2, 0.8]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
              style={{ width: '100%' }}
            />
            <motion.div
              className="absolute -top-1 right-0 h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent"
              animate={{
                opacity: [0, 0.6, 0],
                scaleX: [0.6, 1, 0.6]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2
              }}
              style={{ width: '60%' }}
            />
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default QivratechLogo;